import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import PhotoAlbum from 'react-photo-album';
// v3 ships the layout CSS separately — without it every photo renders at full
// container width and the rows layout does nothing.
import 'react-photo-album/rows.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

import { galleryImages } from '../data/galleryData';
import styles from './Gallery.module.css';
import content from '../data/content.json';
import Seo from '../components/base/Seo';
import CyberLunaWindow from '../components/base/CyberLunaWindow';
import PerformancesShelf from '../components/sections/PerformancesShelf';

const Gallery = () => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const photos = useMemo(() =>
    galleryImages.map(img => ({
      src: img.src,
      // Lets the browser pick a thumbnail-sized file for the grid instead of
      // the 2048px original; the lightbox below still uses img.src.
      srcSet: img.srcSet,
      width: img.width,
      height: img.height,
      alt: img.alt,
      key: img.id,
    })), []);

  const lightboxSlides = useMemo(() =>
    galleryImages.map(img => ({
      src: img.src,
      alt: img.alt,
    })), []);

  const handlePhotoClick = useCallback(({ index }) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        setIsLightboxOpen(false);
        setLightboxIndex(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  useEffect(() => {
    document.body.style.overflow = isLightboxOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isLightboxOpen]);

  return (
    <div className={styles.gallery}>
      <Seo pageKey="gallery" />
      <motion.header
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.hero}
      >
        <h1 className={styles.heroTitle}>{content.gallery.hero.title}</h1>
        <div className={styles.heroAccent} />
      </motion.header>

      <PerformancesShelf />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={styles.gridContainer}
      >
        <CyberLunaWindow
          isStatic
          title={content.gallery.window_title}
          bodyClassName={styles.albumBody}
        >
          <PhotoAlbum
            photos={photos}
            layout="rows"
            targetRowHeight={420}
            spacing={10}
            onClick={handlePhotoClick}
            render={{
              // v3 renders each photo as a <button>; extending it keeps the
              // library's own click and keyboard handling intact.
              button: ({ className, ...rest }, { photo }) => (
                <button
                  {...rest}
                  className={`${className ?? ''} ${styles.photoWrapper}`}
                  aria-label={`Open ${photo.alt}`}
                />
              ),
              extras: () => <div className={styles.photoOverlay} aria-hidden="true" />,
            }}
          />

          {/* Explorer-style status bar */}
          <div className={styles.statusBar}>
            <span className={styles.statusCell}>{photos.length} objects</span>
            <span className={`${styles.statusCell} ${styles.statusHint}`}>
              {content.gallery.status_hint}
            </span>
          </div>
        </CyberLunaWindow>
      </motion.div>

      {/* XP title bar overlay — sits above the YARL lightbox */}
      {isLightboxOpen && (
        <div className={styles.xpBar}>
          <span className={styles.xpBarIcon}>◈</span>
          <span className={styles.xpBarTitle}>gallery_viewer.exe</span>
          {/* Minimize and maximize are decorative here — the viewer is already
              full-screen and neither has anything to do, so they are not
              exposed as controls. Close is the only real button. */}
          <div className={styles.xpBarControls}>
            <span className={`${styles.xpBarBtn} ${styles.xpBarBtnMin}`} aria-hidden="true">
              <span className={styles.xpBarMinIcon} />
            </span>
            <span className={`${styles.xpBarBtn} ${styles.xpBarBtnMax}`} aria-hidden="true">
              <span className={styles.xpBarMaxIcon} />
            </span>
            <button
              className={`${styles.xpBarBtn} ${styles.xpBarBtnClose}`}
              type="button"
              aria-label="Close"
              onClick={() => { setIsLightboxOpen(false); setLightboxIndex(-1); }}
            >✕</button>
          </div>
        </div>
      )}

      <Lightbox
        open={isLightboxOpen}
        index={lightboxIndex}
        close={() => {
          setIsLightboxOpen(false);
          setLightboxIndex(-1);
        }}
        slides={lightboxSlides}
        carousel={{ finite: false }}
        animation={{ fade: 300, swipe: 300 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.97)' } }}
      />
    </div>
  );
};

export default Gallery;
